import React, { Suspense, useContext, useMemo, useState } from "react";
import FullLoader from "components/loader/full-loader";
import { Dropdown, Modal } from "react-bootstrap";
import PermissionMiddleware from "middleware/PermissionMiddleware";
// import { useDeleteData, useFetchData } from "hooks/useApiHooks";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
// import { AppContext } from "contexts/AppContext";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import CreatePage from "./components/create-page";
// import { useDeleteHandler } from "hooks/useDeleteHandler";
// import { buildQueryString } from "utils/queryHelpers";
// import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
// import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import Swal from "sweetalert2";
import { deleteRequest, postRequest, getRequest } from "services/api";
import { ProductCreateProvider } from "contexts/v1/masterData/ProductCreateContext";
import { useUser } from "contexts/v1/masterData/UserContext";
import { useDepartments } from "pages/hr/master/context/DepartmentsContext";
import { useFileUpload } from "hooks/useFileUpload";
import { API_URL } from "utils/constants";

const getDepartmentName = (user) =>
  user?.department?.name ||
  user?.department_name ||
  user?.position?.department?.name ||
  "Tanpa Departemen";

const UsersPage = () => {
  const { users, refetch, Pagination, handleSort, filterOptions } = useUser();
  const groupedUsers = useMemo(() => {
    const groups = users.data.reduce((result, user) => {
      const departmentName = getDepartmentName(user).toUpperCase();
      return {
        ...result,
        [departmentName]: [...(result[departmentName] || []), user],
      };
    }, {});

    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [users.data]);

  const renderSortableHeader = (label, field, className = "") => {
    const isSorted = filterOptions.orderBy === field;
    return (
      <th 
        style={{ cursor: "pointer", userSelect: "none" }} 
        onClick={() => handleSort(field)}
        className={className}
      >
        {label}
        {isSorted && (
          <span className="ms-1">
            {filterOptions.order === "asc" ? "↑" : "↓"}
          </span>
        )}
      </th>
    );
  };
  // console.log(users);
  const [search, setSearch] = useState("");
  // const [currentPage] = useState(1);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showUserinfoModal, setShowUserinfoModal] = useState(false);
  const [userinfoPins, setUserinfoPins] = useState("");
  const [userinfoResponse, setUserinfoResponse] = useState(null);
  const [userinfoLoading, setUserinfoLoading] = useState(false);
  const [collapsedDepts, setCollapsedDepts] = useState({});

  const toggleDept = (deptName) => {
    setCollapsedDepts((prev) => ({
      ...prev,
      [deptName]: !prev[deptName],
    }));
  };

  // const queryString = buildQueryString({
  //   search: search || "",
  //   page: currentPage,
  //   limit: LIMIT_DATA_PER_PAGE,
  // });

  // const { state } = useContext(AppContext);

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await deleteRequest(`/user/${id}`);

        if (response.success) {
          handleSuccessToast(true, "User deleting");
          refetch();
        }
      }
    });
  };

  const { handleError, showCanvasHandler, handleSuccessToast } =
    useCanvasHandler();

  const handleCreate = (e) => {
    e.preventDefault();
    showCanvasHandler(
      "Create User",
      <ProductCreateProvider>
        <CreatePage
          autoRole="user_hr"
          onSuccess={() => {
            handleSuccessToast(true, "User creating");
            refetch();
          }}
          onError={handleError}
        />
      </ProductCreateProvider>
    );
  };

  const handleUpdate = (e, data) => {
    e.preventDefault();
    showCanvasHandler(
      "Update User",
      <ProductCreateProvider>
        <CreatePage
          initialData={data}
          onSuccess={() => {
            handleSuccessToast(true, "User updating");
            refetch();
          }}
          onError={handleError}
        />
      </ProductCreateProvider>
    );
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setSearch(e.target.value);
    }
  };

  const handleSync = async (userId) => {
    try {
      const response = await postRequest("/ahr/fingerspot/sync-users", {
        user_ids: [userId]
      });
      if (response.success) {
        handleSuccessToast(true, "User synced successfully");
      } else {
        handleError("Failed to sync user");
      }
    } catch (error) {
      handleError("Error syncing user");
    }
  };

  const handleSyncAll = async () => {
    const userIds = users.data.map(user => user.id);
    try {
      const response = await postRequest("/ahr/fingerspot/sync-users", {
        user_ids: userIds
      });
      if (response.success) {
        handleSuccessToast(true, "All users synced successfully");
      } else {
        handleError("Failed to sync users");
      }
    } catch (error) {
      handleError("Error syncing users");
    }
  };

  const handleDeleteFingerspot = async (userData) => {
    if (!userData.fingerspot_pin) {
      handleError("User does not have a Fingerspot PIN");
      return;
    }

    try {
      const response = await postRequest("/ahr/fingerspot/delete-users", {
        pins: [userData.fingerspot_pin.toString()]
      });
      if (response.success) {
        handleSuccessToast(true, "User deleted from Fingerspot successfully");
        refetch();
      } else {
        handleError("Failed to delete user from Fingerspot");
      }
    } catch (error) {
      handleError("Error deleting user from Fingerspot");
    }
  };

  const handlePhotoClick = (photoUrl) => {
    setSelectedPhoto(photoUrl);
    setShowPhotoModal(true);
  };

  const handleUploadPhoto = (userData) => {
    showCanvasHandler(
      "Upload Photo",
      <UploadPhotoComponent
        userId={userData.id}
        onSuccess={() => {
          handleSuccessToast(true, "Photo uploaded successfully");
          refetch();
        }}
        onError={handleError}
      />
    );
  };

  const handleGetUserinfo = async () => {
    setUserinfoLoading(true);
    setUserinfoResponse(null);

    try {
      // Parse pins from comma-separated string if provided
      let pinsArray = [];
      if (userinfoPins.trim()) {
        pinsArray = userinfoPins
          .split(',')
          .map(pin => pin.trim())
          .filter(pin => pin !== '');
      }

      // Only include pins in request body if there are pins to send
      const requestBody = pinsArray.length > 0 ? { pins: pinsArray } : {};

      const response = await postRequest("/ahr/fingerspot/get-userinfo", requestBody);
      setUserinfoResponse(response);
    } catch (error) {
      setUserinfoResponse({ error: true, message: error.message || "Failed to get userinfo" });
    } finally {
      setUserinfoLoading(false);
    }
  };

  if (users.loading) return <FullLoader />;
  if (users.error) return <Suspense error={users.error} />;

  return (
    <LayoutDashboard>
      <div className="container-fluid mt-5">
        <div className="table-container">
          <div className="search-container mb-3 d-flex flex-wrap gap-2 align-items-center">
            <SearchInput handleSearch={handleSearch} defaultValue={search} />
            <FilterDropdowns />
            <button className="btn btn-primary" onClick={handleCreate}>
              Create
            </button>
            <button className="btn btn-secondary" onClick={handleSyncAll}>
              Sync All Users
            </button>
            <button 
              className="btn btn-info" 
              onClick={() => {
                setUserinfoPins("");
                setUserinfoResponse(null);
                setShowUserinfoModal(true);
              }}
            >
              Get User Info
            </button>
          </div>
          <div className="table-responsive">
            <table className="table table-hover table-vcenter">
              <thead>
                <tr>
                  <th>No</th>
                  {renderSortableHeader("PIN", "fingerspot_pin")}
                  <th>Photo</th>
                  {renderSortableHeader("User", "name")}
                  {renderSortableHeader("Job", "job")}
                  {renderSortableHeader("Join Date", "join_date")}
                  {renderSortableHeader("Birth Date", "birth_date")}
                  {renderSortableHeader("Age", "age", "text-center")}
                  {renderSortableHeader("Department", "department_id")}
                  {renderSortableHeader("Position", "position_id")}
                  {renderSortableHeader("Day Off", "day_off")}
                  {renderSortableHeader("Email", "email")}
                  {renderSortableHeader("Phone", "phone")}
                  {renderSortableHeader("Rekening", "nomor_rekening")}
                  {/* <th>Fingerspot PIN</th> */}
                  {renderSortableHeader("Finger", "fingerspot_finger", "text-center")}
                  {renderSortableHeader("Face", "fingerspot_face", "text-center")}
                  {/* <th>Password</th> */}
                  {/* <th>RFID</th> */}
                  {renderSortableHeader("Vein", "fingerspot_vein", "text-center")}
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              {users.data.length > 0 && (
                <tbody>
                  {groupedUsers.map(([departmentName, departmentUsers]) => {
                    const isCollapsed = collapsedDepts[departmentName];
                    return (
                      <React.Fragment key={departmentName}>
                        <tr 
                          className="table-primary" 
                          style={{ cursor: "pointer" }} 
                          onClick={() => toggleDept(departmentName)}
                        >
                          <td colSpan="18" className="fw-bold text-primary">
                            <div className="d-flex align-items-center gap-2">
                              {isCollapsed ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-chevron-right" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                  <polyline points="9 6 15 12 9 18"></polyline>
                                </svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-chevron-down" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              )}
                              {departmentName} ({departmentUsers.length})
                            </div>
                          </td>
                        </tr>
                        {!isCollapsed && departmentUsers.map((data, index) => (
                      <tr>
                        <td>{index + 1}</td>
                        <td>{data.fingerspot_pin || '-'}</td>
                        <td>
                          {data.photo_url ? (
                            <img
                              src={data.photo_url}
                              alt="User Photo"
                              style={{ width: '50px', height: '50px', objectFit: 'cover', cursor: 'pointer', borderRadius: '4px' }}
                              onClick={() => handlePhotoClick(data.photo_url)}
                            />
                          ) : (
                            'No Photo'
                          )}
                        </td>
                        <td>{data.name}</td>
                        <td>{data.job || '-'}</td>
                          <td>{data.join_date ? new Date(data.join_date).toLocaleDateString('id-ID') : '-'}</td>
                          <td>{data.birth_date ? new Date(data.birth_date).toLocaleDateString('id-ID') : '-'}</td>
                          <td className="text-center">{data.birth_date ? Math.floor((new Date() - new Date(data.birth_date).getTime()) / 3.15576e+10) : '-'}</td>

                        <td>{data.department?.name || '-'}</td>
                        <td>{data.position?.name || '-'}</td>
                        <td>{data.day_off || '-'}</td>
                        <td><MaskedEmail email={data.email} /></td>
                        <td>{data.phone || '-'}</td>
                        <td>{data.nomor_rekening || '-'}</td>
                        {/* <td>{data.fingerspot_pin || '-'}</td> */}
                        <td className="text-center">
                          <span className="badge" style={{ 
                            backgroundColor: data.fingerspot_finger > 0 ? '#22c55e' : '#ef4444',
                            color: 'white',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem'
                          }}>
                            {data.fingerspot_finger > 0 ? '✓' : '✗'}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="badge" style={{ 
                            backgroundColor: data.fingerspot_face > 0 ? '#22c55e' : '#ef4444',
                            color: 'white',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem'
                          }}>
                            {data.fingerspot_face > 0 ? '✓' : '✗'}
                          </span>
                        </td>
                        {/* <td className="text-center">{data.fingerspot_password ? '✓' : '✗'}</td> */}
                        {/* <td className="text-center">{data.fingerspot_rfid ? '✓' : '✗'}</td> */}
                        <td className="text-center">
                          <span className="badge" style={{ 
                            backgroundColor: data.fingerspot_vein > 0 ? '#22c55e' : '#ef4444',
                            color: 'white',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem'
                          }}>
                            {data.fingerspot_vein > 0 ? '✓' : '✗'}
                          </span>
                        </td>
                        <td className="text-end">
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="default"
                              id="dropdownMenuButton1"
                            >
                              Actions
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item
                                href="#"
                                onClick={(e) => handleUpdate(e, data)}
                              >
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                href="#"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUploadPhoto(data);
                                }}
                              >
                                Upload Photo
                              </Dropdown.Item>
                              <Dropdown.Item
                                href="#"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSync(data.id);
                                }}
                              >
                                Sync
                              </Dropdown.Item>
                              <PermissionMiddleware allowedPermissions={["hr_delete_fingerspot"]}>
                                <Dropdown.Item
                                  href="#"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteFingerspot(data);
                                  }}
                                >
                                  Delete Fingerspot
                                </Dropdown.Item>
                              </PermissionMiddleware>
                              <PermissionMiddleware allowedPermissions={["hr_delete_user"]}>
                                <Dropdown.Item
                                  href="#"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(data.id);
                                  }}
                                >
                                  Remove
                                </Dropdown.Item>
                              </PermissionMiddleware>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                      ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>
          <div className="mt-4">{users.data.length > 0 && Pagination}</div>
        </div>
      </div>
      <Modal show={showPhotoModal} onHide={() => setShowPhotoModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>User Photo</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <img src={selectedPhoto} alt="User Photo" style={{ maxWidth: '100%', maxHeight: '70vh' }} />
        </Modal.Body>
      </Modal>
      
      <Modal show={showUserinfoModal} onHide={() => setShowUserinfoModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Get User Info from Fingerspot</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">PINs (comma separated)</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder='e.g., 61,62,63,64,65'
              value={userinfoPins}
              onChange={(e) => setUserinfoPins(e.target.value)}
            />
            <small className="text-muted">
              Enter PINs separated by commas. Example: 61,62,63,64,65
            </small>
          </div>
          
          <div className="d-flex gap-2 mb-3">
            <button 
              className="btn btn-primary" 
              onClick={handleGetUserinfo}
              disabled={userinfoLoading}
            >
              {userinfoLoading ? 'Loading...' : 'Get User Info'}
            </button>
            <button 
              className="btn btn-outline-secondary" 
              onClick={() => setUserinfoPins("")}
            >
              Clear
            </button>
          </div>

          {userinfoResponse && (
            <div className="mt-3">
              <label className="form-label">Response:</label>
              <pre 
                style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '15px', 
                  borderRadius: '8px',
                  maxHeight: '400px',
                  overflow: 'auto',
                  fontSize: '12px'
                }}
              >
                {JSON.stringify(userinfoResponse, null, 2)}
              </pre>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowUserinfoModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </LayoutDashboard>
  );
};

const UploadPhotoComponent = ({ userId, onSuccess, onError }) => {
  const { file, FileUpload, FileSelectDisplay } = useFileUpload();

  const handleSubmit = async () => {
    if (!file) {
      onError("Please select a file");
      return;
    }
    const formData = new FormData();
    formData.append('photo', file);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/user/${userId}/photo`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          onSuccess();
        } else {
          onError("Failed to upload photo");
        }
      } else {
        onError("Failed to upload photo");
      }
    } catch (error) {
      onError("Error uploading photo");
    }
  };

  return (
    <div>
      <FileUpload />
      <FileSelectDisplay />
      <button className="btn btn-primary mt-3" onClick={handleSubmit}>
        Upload
      </button>
    </div>
  );
};

export default UsersPage;

const ExpandableText = ({ text = "", maxLength = 20 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const displayText =
    isExpanded || text.length <= maxLength
      ? text
      : text.slice(0, maxLength) + "...";

  return (
    <span>
      {displayText}
      {text.length > maxLength && (
        <button
          onClick={toggleExpanded}
          style={{
            background: "none",
            border: "none",
            color: "blue",
            cursor: "pointer",
            marginLeft: "5px",
            padding: 0,
          }}
        >
          {isExpanded ? "Show less" : "Read more"}
        </button>
      )}
    </span>
  );
};

const SearchInput = () => {
  const { handleSearch, filterOptions } = useUser();
  const [search, setSearch] = useState(filterOptions.search || "");

  const handleSearchInput = (e) => {
    if (e.key === "Enter") {
      if (!search.trim()) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Search input cannot be empty!",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        handleSearch(handleSearch);
      }
    }
  };

  return (
    <div className="input-icon">
      <input
        type="text"
        className="form-control"
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={handleSearch}
      />
      <span className="input-icon-addon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="icon icon-tabler icon-tabler-search"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <circle cx="10" cy="10" r="7" />
          <line x1="21" y1="21" x2="15" y2="15" />
        </svg>
      </span>
    </div>
  );
};

const MaskedEmail = ({ email }) => {
  const [isHovered, setIsHovered] = useState(false);

  const maskEmail = (email) => {
    const [local, domain] = email.split('@');
    const visibleLocal = Math.min(2, local.length);
    const maskedLocal = local.slice(0, visibleLocal) + '*'.repeat(Math.max(0, local.length - visibleLocal));
    const visibleDomain = Math.min(3, domain.length);
    const maskedDomain = '*'.repeat(Math.max(0, domain.length - visibleDomain)) + domain.slice(-visibleDomain);
    return `${maskedLocal}@${maskedDomain}`;
  };

  return (
    <span
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: 'pointer' }}
    >
      {isHovered ? email : maskEmail(email)}
    </span>
  );
};

const FilterDropdowns = () => {
  const { filterOptions, handleFilter, clearFilters } = useUser();
  const { departments } = useDepartments();

  const filterOptions_list = [
    { value: "", label: "All" },
    { value: "1", label: "Registered" },
    { value: "0", label: "Not Registered" },
  ];

  const hasActiveFilters = 
    filterOptions.fingerspot_finger || 
    filterOptions.fingerspot_face || 
    filterOptions.fingerspot_vein ||
    filterOptions.department_id;

  return (
    <div className="d-flex flex-wrap gap-2 align-items-center">
      <select
        className="form-select"
        style={{ width: 'auto', minWidth: '150px' }}
        value={filterOptions.department_id || ""}
        onChange={(e) => handleFilter('department_id', e.target.value)}
      >
        <option value="">All Departments</option>
        {departments.data?.map((dept) => (
          <option key={dept.id} value={dept.id}>{dept.name}</option>
        ))}
      </select>

      <select
        className="form-select"
        style={{ width: 'auto', minWidth: '120px' }}
        value={filterOptions.fingerspot_finger}
        onChange={(e) => handleFilter('fingerspot_finger', e.target.value)}
      >
        <option value="">Finger</option>
        {filterOptions_list.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      <select
        className="form-select"
        style={{ width: 'auto', minWidth: '120px' }}
        value={filterOptions.fingerspot_face}
        onChange={(e) => handleFilter('fingerspot_face', e.target.value)}
      >
        <option value="">Face</option>
        {filterOptions_list.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      <select
        className="form-select"
        style={{ width: 'auto', minWidth: '120px' }}
        value={filterOptions.fingerspot_vein}
        onChange={(e) => handleFilter('fingerspot_vein', e.target.value)}
      >
        <option value="">Vein</option>
        {filterOptions_list.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      {hasActiveFilters && (
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={clearFilters}
          title="Clear Filters"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="icon icon-tabler icon-tabler-x"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
};
